import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";
import { AuthService } from "../auth";
import { catchError, map, of } from "rxjs";
import { Permissions, UserModel } from "./api/models";

@Injectable({ providedIn: 'root' })
// protects routes from users without supervisor permissions
export class SupervisorAuthGuard {
  constructor(private authService: AuthService, private router: Router) {
  }
  
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    // First check if the user is signed in
    if (!this.authService.isSignedIn()) {
      console.warn(`SupervisorAuthGuard: '${state.url}' - User not signed in`, this.authService.tokenInfo, this.authService.userInfo);
      
      // Redirect to signin if not authenticated
      this.router.navigate(['/auth/signin'], {
        queryParams: { returnUrl: state.url }
      });
      
      return false;
    }

    if (this.hasSupervisorPermission(this.authService.userInfo)) {
      return true;
    }

    // Token can be restored before the profile is available (full reload / login redirect).
    // Wait for the profile instead of treating a missing userInfo as unauthorized.
    if (this.authService.userInfo == null) {
      return this.authService.getUserInfo().pipe(
        map((user) => this.allowOrRejectSupervisor(user, state)),
        catchError(() => {
          this.router.navigate(['/not-authorized']);
          return of(false);
        })
      );
    }
    
    return this.allowOrRejectSupervisor(this.authService.userInfo, state);
  }

  private hasSupervisorPermission(userInfo: UserModel | null | undefined): boolean {
    return !!userInfo?.Permissions?.includes(Permissions.SparkApp_Supervisor);
  }

  private allowOrRejectSupervisor(userInfo: UserModel | null | undefined, state: RouterStateSnapshot): boolean {
    if (this.hasSupervisorPermission(userInfo)) {
      return true;
    }

    console.warn(`SupervisorAuthGuard: '${state.url}' - User lacks supervisor permissions`, userInfo);
    this.router.navigate(['/not-authorized']);
    return false;
  }
} 